import { useTranslation } from "react-i18next"
import { List } from "@/components/ui/list"
import { hasDeBetterDocRecommendedCareProviders } from "@/modules/patients/detail/better-doc/de/lib/has-de-better-doc-recommended-care-providers"
import { CarePlanViewer } from "@/modules/patients/detail/progress-note/documents/care-plan-summary-document-preview"
import { checkFileRegeneration } from "@/modules/patients/detail/progress-note/documents/check-file-regeneration"
import { ConsultationReportDocument } from "@/modules/patients/detail/progress-note/documents/consultation-report-document"
import { GeneralAttestationDocument } from "@/modules/patients/detail/progress-note/documents/general-attestation-document"
import { LaboratoryPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/laboratory-prescription-document"
import { LegalGuardianAttestation } from "@/modules/patients/detail/progress-note/documents/legal-guardian-attestation"
import { MeasLaboratoryPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/meas-laboratory-prescription-document"
import { MeasRadiologyPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/meas-radiology-prescription-document"
import { MedicalReportAttachments } from "@/modules/patients/detail/progress-note/documents/medical-report-attachments"
import { NeuropsychologicDiagnosticsPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/neuropsychologic-diagnostic-prescription-document"
import { NutritionalCounsellingDocument } from "@/modules/patients/detail/progress-note/documents/nutritional-counselling-document"
import { PhysiotherapyDocument } from "@/modules/patients/detail/progress-note/documents/physiotherapy-document"
import { PrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/prescription-document"
import { PsychologicalPsychoTherapy } from "@/modules/patients/detail/progress-note/documents/psychological-psychotherapy"
import ReferralLetterDocument from "@/modules/patients/detail/progress-note/documents/referral-letter-document"
import { SickLeaveCertificateDocument } from "@/modules/patients/detail/progress-note/documents/sick-leave-document"
import { TherapyPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/therapy-prescription-document"
import { TreatmentPlanDocument } from "@/modules/patients/detail/progress-note/documents/treatment-plan-document"
import { ProgressNoteDocument } from "@/modules/patients/detail/progress-note/types/document"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import {
  formatProgressNoteDocuments,
  getCarePlanSummaryDocumentId,
} from "@/modules/patients/detail/store/useDocumentStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { ChProgressNoteDocument, DeProgressNoteDocument } from "@/modules/patients/types/ProgressNoteDocument"

interface DocumentListProps {
  progressNote: ProgressNoteData
  isEditable?: boolean
  isConclusion?: boolean
  showTreatmentPlan?: boolean
  onSelect?: ({ id, type, renderFn }: ProgressNoteDocument) => void
  isPreviewConclude?: boolean
  diagnosisCode?: string
}

const DocumentList = ({
  progressNote,
  isEditable = true,
  showTreatmentPlan = false,
  isConclusion,
  onSelect,
  isPreviewConclude = false,
  diagnosisCode,
}: DocumentListProps) => {
  const countryCode = useCountryParam()
  const { id: progressNoteId, followUpAppointment, __typename } = progressNote
  const { t } = useTranslation()

  switch (countryCode) {
    case "CH": {
      const progressNoteData = {
        ...formatProgressNoteDocuments(progressNote, isPreviewConclude),
      } as ChProgressNoteDocument

      if (!isPreviewConclude) {
        progressNoteData.carePlanSummary = null
      }

      if (Object.values(progressNoteData).every((value) => !value)) return null

      const fileUploadRequest = __typename === "ch_ProgressNote" ? followUpAppointment?.fileUploadRequest : undefined
      const medgateConnectOrder =
        __typename === "ch_ProgressNote" ? followUpAppointment?.medgateConnectOrder : undefined

      const {
        carePlanSummary,
        prescription,
        sickLeave,
        attestationGuardian,
        physiotherapy,
        ch_nutritionalPrescription,
        psychologicalPsychotherapy,
        generalAttestation,
        referralLetter,
        measLaboratoryPrescription,
        ch_measRadiologyPrescription,
        medicalReportAttachments,
        ch_neuropsychologicDiagnosticPrescription,
        ch_laboratoryPrescription,
        ch_consultationReport,
      } = progressNoteData || {}

      const hasDocument = Object.values(progressNoteData ?? {}).some((value) => value)
      const hasCareProviderDocuments = !!referralLetter || !!ch_consultationReport

      const isFileOutdated = (doc: Parameters<typeof checkFileRegeneration>[0]) =>
        isPreviewConclude && checkFileRegeneration(doc, fileUploadRequest, medgateConnectOrder).needsRegeneration

      return (
        <>
          {hasDocument && isPreviewConclude && (
            <div className="text-on-surface-subdued my-3.5 text-sm font-medium">
              {t("patient.conclude.forThePatient")}
            </div>
          )}
          <List className={!isEditable ? "grid gap-2" : "mx-4 mb-4"} data-testid="preview-document-list">
            {showTreatmentPlan && carePlanSummary?.treatmentPlan && (
              <TreatmentPlanDocument
                id={carePlanSummary.id}
                treatmentPlan={carePlanSummary.treatmentPlan}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={!!carePlanSummary.signedAt}
                onSelect={() => {
                  const id = carePlanSummary?.id || getCarePlanSummaryDocumentId(progressNoteId)

                  onSelect?.({
                    ...carePlanSummary,
                    renderFn: () => (
                      <CarePlanViewer
                        id={id}
                        progressNoteId={progressNoteId}
                        fileId={carePlanSummary.fileId}
                        signedAt={carePlanSummary.signedAt}
                      />
                    ),
                  })
                }}
              />
            )}
            {prescription && (
              <PrescriptionDocument
                key={prescription.id}
                id={prescription.id}
                prescriptionRef={prescription}
                healthcareProviderId={prescription.healthcareProviderId}
                isSigned={isFileOutdated(prescription) ? false : !!prescription.signedAt}
                progressNoteId={progressNoteId}
                medicationIds={prescription.medications.map((i) => i.id)}
                isEditable={isEditable}
                isConclusion={isConclusion}
                onSelect={() =>
                  onSelect?.({
                    ...prescription,
                    progressNoteId,
                  })
                }
                diagnosisCode={diagnosisCode}
              />
            )}
            {sickLeave && (
              <SickLeaveCertificateDocument
                key={sickLeave.id}
                sickLeaveRef={sickLeave}
                isSigned={isFileOutdated(sickLeave) ? false : !!sickLeave.signedAt}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isConclusion={isConclusion}
                onSelect={() =>
                  onSelect?.({
                    ...sickLeave,
                    progressNoteId,
                  })
                }
              />
            )}
            {attestationGuardian && (
              <LegalGuardianAttestation
                key={attestationGuardian.id}
                attestationGuardianRef={attestationGuardian}
                isSigned={isFileOutdated(attestationGuardian) ? false : !!attestationGuardian.signedAt}
                isEditable={isEditable}
                isConclusion={isConclusion}
                onSelect={() =>
                  onSelect?.({
                    ...attestationGuardian,
                    progressNoteId,
                  })
                }
              />
            )}
            {physiotherapy && (
              <PhysiotherapyDocument
                key={physiotherapy.id}
                id={physiotherapy.id}
                physiotherapyRef={physiotherapy}
                isSigned={isFileOutdated(physiotherapy) ? false : !!physiotherapy.signedAt}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isConclusion={isConclusion}
                healthcareProviderId={physiotherapy.healthcareProviderId ?? ""}
                treatmentUnit={physiotherapy.treatmentUnit}
                onSelect={() =>
                  onSelect?.({
                    ...physiotherapy,
                    progressNoteId,
                  })
                }
              />
            )}
            {ch_nutritionalPrescription && (
              <NutritionalCounsellingDocument
                key={ch_nutritionalPrescription.id}
                nutritionalCounsellingRef={ch_nutritionalPrescription}
                isSigned={isFileOutdated(ch_nutritionalPrescription) ? false : !!ch_nutritionalPrescription.signedAt}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isConclusion={isConclusion}
                healthcareProviderId={ch_nutritionalPrescription.healthcareProviderId ?? ""}
                onSelect={() =>
                  onSelect?.({
                    ...ch_nutritionalPrescription,
                    progressNoteId,
                  })
                }
              />
            )}
            {psychologicalPsychotherapy && (
              <PsychologicalPsychoTherapy
                key={psychologicalPsychotherapy.id}
                psychologicalPsychoTherapyRef={psychologicalPsychotherapy}
                isSigned={isFileOutdated(psychologicalPsychotherapy) ? false : !!psychologicalPsychotherapy.signedAt}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isConclusion={isConclusion}
                onSelect={() =>
                  onSelect?.({
                    ...psychologicalPsychotherapy,
                    progressNoteId,
                  })
                }
              />
            )}
            {generalAttestation && (
              <GeneralAttestationDocument
                key={generalAttestation.id}
                generalAttestationRef={generalAttestation}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={isFileOutdated(generalAttestation) ? false : !!generalAttestation.signedAt}
                onSelect={() =>
                  onSelect?.({
                    ...generalAttestation,
                    progressNoteId,
                  })
                }
              />
            )}
            {!isPreviewConclude && referralLetter && (
              <ReferralLetterDocument
                key={referralLetter.id}
                fragmentRef={referralLetter}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isSigned={isFileOutdated(referralLetter) ? false : !!referralLetter.signedAt}
                isConclusion={isConclusion}
                onSelect={() =>
                  onSelect?.({
                    ...referralLetter,
                    progressNoteId,
                  })
                }
              />
            )}
            {!isPreviewConclude && ch_consultationReport && (
              <ConsultationReportDocument
                key={ch_consultationReport.id}
                fragmentRef={ch_consultationReport}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isSigned={isFileOutdated(ch_consultationReport) ? false : !!ch_consultationReport.signedAt}
                isConclusion={isConclusion}
                onSelect={() =>
                  onSelect?.({
                    ...ch_consultationReport,
                    progressNoteId,
                  })
                }
              />
            )}
            {measLaboratoryPrescription && (
              <MeasLaboratoryPrescriptionDocument
                id={measLaboratoryPrescription.id}
                key={measLaboratoryPrescription.id}
                measLaboratoryPrescriptionRef={measLaboratoryPrescription}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isSigned={isFileOutdated(measLaboratoryPrescription) ? false : !!measLaboratoryPrescription.signedAt}
                isConclusion={isConclusion}
                fileUploadRequest={fileUploadRequest}
                medgateConnectOrder={medgateConnectOrder}
                onSelect={() =>
                  onSelect?.({
                    ...measLaboratoryPrescription,
                    progressNoteId,
                    fileUploadRequest,
                    medgateConnectOrder,
                  })
                }
              />
            )}
            {ch_measRadiologyPrescription && (
              <MeasRadiologyPrescriptionDocument
                key={ch_measRadiologyPrescription.id}
                id={ch_measRadiologyPrescription.id}
                measRadiologyPrescriptionRef={ch_measRadiologyPrescription}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={
                  isFileOutdated(ch_measRadiologyPrescription) ? false : !!ch_measRadiologyPrescription.signedAt
                }
                progressNoteId={progressNoteId}
                fileUploadRequest={fileUploadRequest}
                medgateConnectOrder={medgateConnectOrder}
                onSelect={() =>
                  onSelect?.({
                    ...ch_measRadiologyPrescription,
                    progressNoteId,
                    fileUploadRequest,
                    medgateConnectOrder,
                  })
                }
              />
            )}
            {ch_neuropsychologicDiagnosticPrescription && (
              <NeuropsychologicDiagnosticsPrescriptionDocument
                key={ch_neuropsychologicDiagnosticPrescription.id}
                id={ch_neuropsychologicDiagnosticPrescription.id}
                neuropsychologicDiagnosticPrescriptionRef={ch_neuropsychologicDiagnosticPrescription}
                medicalPrescriptions={ch_neuropsychologicDiagnosticPrescription.medicalPrescriptions}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={
                  isFileOutdated(ch_neuropsychologicDiagnosticPrescription)
                    ? false
                    : !!ch_neuropsychologicDiagnosticPrescription.signedAt
                }
                progressNoteId={progressNoteId}
                onSelect={() =>
                  onSelect?.({
                    ...ch_neuropsychologicDiagnosticPrescription,
                    progressNoteId,
                  })
                }
              />
            )}
            {ch_laboratoryPrescription && (
              <LaboratoryPrescriptionDocument
                key={ch_laboratoryPrescription.id}
                id={ch_laboratoryPrescription.id}
                laboratoryPrescriptionRef={ch_laboratoryPrescription}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={isFileOutdated(ch_laboratoryPrescription) ? false : !!ch_laboratoryPrescription.signedAt}
                progressNoteId={progressNoteId}
                medgateConnectOrder={medgateConnectOrder}
                onSelect={() =>
                  onSelect?.({
                    ...ch_laboratoryPrescription,
                    progressNoteId,
                    medgateConnectOrder,
                  })
                }
              />
            )}
            {!isConclusion && (
              <MedicalReportAttachments
                medicalReportAttachments={medicalReportAttachments?.attachments}
                isEditMode={isEditable}
              />
            )}
          </List>
          {isPreviewConclude && hasCareProviderDocuments && (
            <>
              <div className="text-on-surface-subdued my-3.5 text-sm font-medium">
                {t("patient.conclude.forExternalCareProvider")}
              </div>
              <List className={!isEditable ? "grid gap-2" : "mx-4 mb-4"}>
                {referralLetter && (
                  <ReferralLetterDocument
                    key={referralLetter.id}
                    fragmentRef={referralLetter}
                    progressNoteId={progressNoteId}
                    isEditable={isEditable}
                    isSigned={isFileOutdated(referralLetter) ? false : !!referralLetter.signedAt}
                    isConclusion={isConclusion}
                    onSelect={() =>
                      onSelect?.({
                        ...referralLetter,
                        progressNoteId,
                      })
                    }
                  />
                )}
                {ch_consultationReport && (
                  <ConsultationReportDocument
                    key={ch_consultationReport.id}
                    fragmentRef={ch_consultationReport}
                    progressNoteId={progressNoteId}
                    isEditable={isEditable}
                    isSigned={isFileOutdated(ch_consultationReport) ? false : !!ch_consultationReport.signedAt}
                    isConclusion={isConclusion}
                    onSelect={() =>
                      onSelect?.({
                        ...ch_consultationReport,
                        progressNoteId,
                      })
                    }
                  />
                )}
              </List>
            </>
          )}
        </>
      )
    }

    case "DE": {
      const progressNoteData = {
        ...formatProgressNoteDocuments(progressNote, isPreviewConclude),
      } as DeProgressNoteDocument

      if (!isPreviewConclude) {
        progressNoteData.carePlanSummary = null
      }

      if (Object.values(progressNoteData).every((value) => !value)) return null

      const {
        carePlanSummary,
        attestationGuardian,
        sickLeave,
        therapyPrescription,
        generalAttestation,
        referralLetter,
        prescription,
        medicalReportAttachments,
      } = progressNoteData || {}

      const isFileOutdated = (doc: Parameters<typeof checkFileRegeneration>[0]) =>
        isPreviewConclude && checkFileRegeneration(doc).needsRegeneration

      const isReferralLetterForPatient = hasDeBetterDocRecommendedCareProviders(progressNote)
      const hasDocumentForPatient = Object.entries(progressNoteData ?? {}).some(([documentKey, value]) =>
        documentKey === "referralLetter" ? isReferralLetterForPatient && !!value : !!value,
      )
      const hasCareProviderDocuments = !!referralLetter && !isReferralLetterForPatient

      return (
        <>
          {hasDocumentForPatient && isPreviewConclude && (
            <div className="text-on-surface-subdued my-3.5 text-sm font-medium">
              {t("patient.conclude.forThePatient")}
            </div>
          )}
          <List className={!isEditable ? "grid gap-2" : "mx-4 mb-4"}>
            {showTreatmentPlan && carePlanSummary?.treatmentPlan && (
              <TreatmentPlanDocument
                id={carePlanSummary.id}
                treatmentPlan={carePlanSummary.treatmentPlan}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={!!carePlanSummary.signedAt}
                onSelect={() => {
                  const id = carePlanSummary?.id || getCarePlanSummaryDocumentId(progressNoteId)

                  onSelect?.({
                    ...carePlanSummary,
                    renderFn: () => (
                      <CarePlanViewer
                        id={id}
                        progressNoteId={progressNoteId}
                        fileId={carePlanSummary.fileId}
                        signedAt={carePlanSummary.signedAt}
                      />
                    ),
                  })
                }}
              />
            )}
            {prescription && (
              <PrescriptionDocument
                key={prescription.id}
                id={prescription.id}
                prescriptionRef={prescription}
                progressNoteId={progressNoteId}
                medicationIds={prescription.medications.map((i) => i.id)}
                isEditable={isEditable}
                isSigned={isFileOutdated(prescription) ? false : !!prescription.signedAt}
                isConclusion={isConclusion}
                onSelect={() =>
                  onSelect?.({
                    ...prescription,
                    progressNoteId,
                  })
                }
                diagnosisCode={diagnosisCode}
              />
            )}
            {attestationGuardian && (
              <LegalGuardianAttestation
                key={attestationGuardian.id}
                attestationGuardianRef={attestationGuardian}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={isFileOutdated(attestationGuardian) ? false : !!attestationGuardian.signedAt}
                onSelect={() =>
                  onSelect?.({
                    ...attestationGuardian,
                    progressNoteId,
                  })
                }
              />
            )}
            {sickLeave && (
              <SickLeaveCertificateDocument
                key={sickLeave.id}
                sickLeaveRef={sickLeave}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={isFileOutdated(sickLeave) ? false : !!sickLeave.signedAt}
                onSelect={() =>
                  onSelect?.({
                    ...sickLeave,
                    progressNoteId,
                  })
                }
              />
            )}
            {therapyPrescription && (
              <TherapyPrescriptionDocument
                key={therapyPrescription.id}
                therapyPrescriptionRef={therapyPrescription}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={isFileOutdated(therapyPrescription) ? false : !!therapyPrescription.signedAt}
                onSelect={() =>
                  onSelect?.({
                    ...therapyPrescription,
                    progressNoteId,
                  })
                }
              />
            )}
            {generalAttestation && (
              <GeneralAttestationDocument
                key={generalAttestation.id}
                generalAttestationRef={generalAttestation}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={isFileOutdated(generalAttestation) ? false : !!generalAttestation.signedAt}
                onSelect={() =>
                  onSelect?.({
                    ...generalAttestation,
                    progressNoteId,
                  })
                }
              />
            )}
            {(!isPreviewConclude || isReferralLetterForPatient) && referralLetter && (
              <ReferralLetterDocument
                key={referralLetter.id}
                fragmentRef={referralLetter}
                progressNoteId={progressNoteId}
                isEditable={isEditable}
                isConclusion={isConclusion}
                isSigned={isFileOutdated(referralLetter) ? false : !!referralLetter.signedAt}
                isSignMode
                onSelect={() =>
                  onSelect?.({
                    ...referralLetter,
                    progressNoteId,
                  })
                }
              />
            )}
            {!isConclusion && (
              <MedicalReportAttachments
                medicalReportAttachments={medicalReportAttachments?.attachments}
                isEditMode={isEditable}
              />
            )}
          </List>
          {isPreviewConclude && hasCareProviderDocuments && (
            <>
              <div className="text-on-surface-subdued my-3.5 text-sm font-medium">
                {t("patient.conclude.forExternalCareProvider")}
              </div>
              <List className={!isEditable ? "grid gap-2" : "mx-4 mb-4"}>
                {referralLetter && (
                  <ReferralLetterDocument
                    key={referralLetter.id}
                    fragmentRef={referralLetter}
                    progressNoteId={progressNoteId}
                    isEditable={isEditable}
                    isConclusion={isConclusion}
                    isSigned={isFileOutdated(referralLetter) ? false : !!referralLetter.signedAt}
                    isSignMode
                    onSelect={() =>
                      onSelect?.({
                        ...referralLetter,
                        progressNoteId,
                      })
                    }
                  />
                )}
              </List>
            </>
          )}
        </>
      )
    }
  }
}

export { DocumentList }
